import { yupResolver } from "@hookform/resolvers/yup";
import { AutoCompleteProps } from "antd";
import { findOnlineDriversLocationApi, manualAssignApi } from "api/dispatch";
import { fleetModelsApi } from "api/fleetModel";
// Removed Google geocoding - only using local places API
import { calculateDistanceApi } from "api/distance";
import { placeSearchApi } from "api/place";
import { cancelTripRequestByIdApi, startTripRequestByIdApi, IStartTripRequestInput } from "api/tripRequest";
import { GOOGLE_API_KEY } from "constants/common";
import { useNotificationContext } from "contexts/NotificationContext";
import { useSocketIoContext } from "contexts/SocketIoContext";
import {
  IDispatchInput,
  IDispatchTripInput,
  RequestStatus,
  ILocation,
  IDriverLocation,
  ITripRequestSearchForDriverResponse,
  ActiveMarkerType,
} from "models/dispatch";
import { IFleetModel } from "models/fleetModel";
import { useCallback, useEffect, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import dispatchValidator from "validators/dispatch/dispatch.validator";

export default function useDispatch() {
  const intervalRef = useRef<NodeJS.Timer>();
  const { socket } = useSocketIoContext();
  // Track drivers that have been marked as offline to prevent them from reappearing
  const offlineDriverIdsRef = useRef<Set<string>>(new Set());
  // Track if the dispatch request was cancelled by user
  const isCancelledRef = useRef<boolean>(false);
  // Track the trip request ID if available (for cancelling via socket)
  const tripRequestIdRef = useRef<string | null>(null);
  const [locationName, setLocationName] = useState<{
    pickup: string | undefined;
    dropOff: string | undefined;
  }>({
    pickup: undefined,
    dropOff: undefined,
  });
  const [tripEstimation, setTripEstimation] = useState<{
    distance: string;
    duration: string;
    price: number | null;
    error: string | null;
  }>();
  const [pickupSuggestion, setPickupSuggestion] = useState<{
    local: AutoCompleteProps["options"];
  }>({ local: [] });
  const [pickupSuggestionLoading, setPickupSuggestionLoading] = useState<boolean>(false);
  const [dropOffSuggestion, setDropOffSuggestion] = useState<{
    local: AutoCompleteProps["options"];
  }>({ local: [] });
  const [dropOffSuggestionLoading, setDropOffSuggestionLoading] = useState<boolean>(false);
  const [requestLoading, setRequestLoading] = useState<{
    loading: boolean;
    message?: string;
  }>({ loading: false });
  const [models, setModels] = useState<{
    data: IFleetModel[];
    loading: boolean;
  }>({ data: [], loading: true });
  const { showError, showSuccess } = useNotificationContext();
  const { control, handleSubmit, setValue, watch } = useForm<IDispatchInput>({
    resolver: yupResolver(dispatchValidator),
  });
  const [driverLocation, setDriverLocation] = useState<IDriverLocation[]>([]);
  const [currentLocation, setCurrentLocation] = useState<ILocation>({
    lat: 13.496494229233356,
    lng: 39.47494624044569,
  });
  const [activeMarkerType, setActiveMarkerType] = useState<ActiveMarkerType>();
  const [pickupLocation, setPickupLocation] = useState<ILocation>();
  const [dropOffLocation, setDropOffLocation] = useState<ILocation>();
  // FIX P2: Selected driver info panel state
  const [selectedDriverInfo, setSelectedDriverInfo] = useState<IDriverLocation | null>(null);
  // FIX P2: Dispatch progress tracking
  const [dispatchProgress, setDispatchProgress] = useState<{
    active: boolean;
    currentDriver: string | null;
    driversTriedCount: number;
    messages: string[];
  }>({ active: false, currentDriver: null, driversTriedCount: 0, messages: [] });
  // FIX P2: Driver search/filter
  const [driverSearchQuery, setDriverSearchQuery] = useState<string>("");
  const [pickupDebounceTimer, setPickupDebounceTimer] =
    useState<NodeJS.Timer>();
  const [dropOffDebounceTimer, setDropOffDebounceTimer] =
    useState<NodeJS.Timer>();
  const [pickupToken, setPickupToken] = useState<string>();
  const [dropOffToken, setDropOffToken] = useState<string>();
  const [pickupLocationDisable, setPickupLocationDisable] =
    useState<boolean>(false);
  const [dropOffLocationDisable, setDropOffLocationDisable] =
    useState<boolean>(false);
  const watchDropOffLocation = watch("drop_off_location");
  const watchPickupLocation = watch("pickup_location");
  const watchDriverId = watch("driver_id");
  const watchFleetModelId = watch("fleet_model_id");

  useEffect(() => {
    if (
      pickupLocation?.lat &&
      pickupLocation?.lng &&
      dropOffLocation?.lat &&
      dropOffLocation.lng
    ) {
      const fetchDistance = async () => {
        try {
          // Use backend API instead of Google Distance Matrix API
          // This uses OSRM (free) instead of Google Maps ($5/1000 calls)
          const { data, error } = await calculateDistanceApi(
            pickupLocation.lat,
            pickupLocation.lng,
            dropOffLocation.lat,
            dropOffLocation.lng
          );

          if (data) {
            const distance = data.distance_text;
            const duration = data.duration_text;
            const distanceValue = data.distance * 1000; // Convert km to meters
            const durationValue = data.duration;

            let price: number | null = null;
            if (watchFleetModelId) {
              const fleetModel = models.data.find(
                (model) => model.id === watchFleetModelId
              );
              if (fleetModel?.name) {
                price =
                  fleetModel.base_fare +
                  fleetModel.price_per_minute * (durationValue / 60) +
                  (fleetModel.price_per_kilo_meter * distanceValue) / 1000;
              }
            }
            setTripEstimation({ distance, duration, price, error: null });
          } else {
            setTripEstimation({
              distance: "",
              duration: "",
              price: null,
              error:
                error?.message ||
                "Sorry, we can't get a route to your destination. The destination or your location may be on a lake or off route. Please check the name or try a nearby location. Consider using a specific landmark or address near the lake.",
            });
          }
        } catch (err) {
          console.error("Error calculating distance:", err);
          setTripEstimation({
            distance: "",
            duration: "",
            price: null,
            error:
              "Sorry, we can't get a route to your destination. The destination or your location may be on a lake or off route. Please check the name or try a nearby location. Consider using a specific landmark or address near the lake.",
          });
        }
      };

      fetchDistance();
    }
    // eslint-disable-next-line
  }, [
    pickupLocation?.lat,
    pickupLocation?.lng,
    dropOffLocation?.lat,
    dropOffLocation?.lng,
    watchFleetModelId,
  ]);

  useEffect(() => {
    fetchFleetModel();
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (socket !== undefined) {
      socket?.on("trip-error", onTripError);
      socket?.on("trip", onTrip);
      socket?.on("trip_status_update", onTripStatusUpdate);
      socket?.on("trip_updated", onTripStatusUpdate);
      fetchDriversLocation();
      intervalRef.current = setInterval(fetchDriversLocation, 5000);
      // Removed navigator.geolocation so map stays on default (Mekelle)
    }
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
      socket?.off("trip-error", onTripError);
      socket?.off("trip", onTrip);
      socket?.off("trip_status_update", onTripStatusUpdate);
      socket?.off("trip_updated", onTripStatusUpdate);
    };
  }, [socket]); // eslint-disable-line

  useEffect(() => {
    const token =
      dropOffToken ??
      `${Math.random().toString(36).substring(2)}-${Date.now()}`;
    if ((watchDropOffLocation ?? "") === "" || dropOffToken === undefined) {
      setDropOffToken(token);
    }
    if (dropOffDebounceTimer !== undefined) clearTimeout(dropOffDebounceTimer);
    if (watchDropOffLocation?.length > 0) {
      setDropOffSuggestionLoading(true);
      setDropOffDebounceTimer(
        setTimeout(() => onDropOffChange(watchDropOffLocation, token), 500)
      );
    } else {
      setDropOffSuggestionLoading(false);
    }
  }, [watchDropOffLocation]); // eslint-disable-line

  useEffect(() => {
    const token =
      pickupToken ?? `${Math.random().toString(36).substring(2)}-${Date.now()}`;
    if ((watchPickupLocation ?? "") === "" || pickupToken === undefined) {
      setPickupToken(token);
    }
    if (pickupDebounceTimer !== undefined) clearTimeout(pickupDebounceTimer);
    if (watchPickupLocation?.length > 0) {
      setPickupSuggestionLoading(true);
      setPickupDebounceTimer(
        setTimeout(() => onPickupChange(watchPickupLocation, token), 500)
      );
    } else {
      setPickupSuggestionLoading(false);
    }
  }, [watchPickupLocation]); // eslint-disable-line

  const fetchDriversLocation = async () => {
    // console.log("🔄 Fetching drivers location...", new Date().toISOString());
    const { data, error } = await findOnlineDriversLocationApi(currentLocation);
    if (data && data.data) {
      let onlineDrivers = data.data;
      if (data.data.length > 0 && 'is_online' in data.data[0]) {
        onlineDrivers = onlineDrivers.filter((driver: IDriverLocation) => driver.is_online === true);
      }
      if (onlineDrivers.length > 0 && 'last_updated' in onlineDrivers[0]) {
        const fiveMinutesAgo = new Date(Date.now() - 5 * 60 * 1000);
        onlineDrivers = onlineDrivers.filter((driver: IDriverLocation) => {
          if (driver.last_updated) {
            return new Date(driver.last_updated) > fiveMinutesAgo;
          }
          return true;
        });
      }
      setDriverLocation(onlineDrivers);
    } else {
      setDriverLocation([]);
    }
  };

  const onTrip = useCallback((data: ITripRequestSearchForDriverResponse) => {
    if (data.data?.id) {
      tripRequestIdRef.current = data.data.id;
    }
    if (isCancelledRef.current) return;

    if (data.status === RequestStatus.loading) {
      setRequestLoading({ loading: true, message: data.message });
      // FIX P2: Track dispatch progress for UI
      setDispatchProgress(prev => ({
        ...prev,
        active: true,
        driversTriedCount: prev.driversTriedCount + 1,
        messages: [...prev.messages.slice(-9), `${new Date().toLocaleTimeString()}: ${data.message}`],
      }));
    } else if (data.status === RequestStatus.not_found || data.status === RequestStatus.NOT_FOUND) {
      console.log("⚠️ No drivers found during dispatch search");
      setRequestLoading({ loading: false });
      isCancelledRef.current = false; // Reset cancellation flag
      tripRequestIdRef.current = null; // Reset trip request ID
      // FIX P2: Reset dispatch progress
      setDispatchProgress({ active: false, currentDriver: null, driversTriedCount: 0, messages: [] });
      showError(data.message);
      // After dispatch search fails, refresh driver list to ensure we have latest data
      // Wait a bit before fetching to avoid race condition
      setTimeout(() => {
        console.log("🔄 Refreshing driver list after failed dispatch search...");
        fetchDriversLocation();
      }, 1000);
    } else if (data.status === RequestStatus.accepted) {
      setRequestLoading({ loading: false });

      // Start the trip request to send information to driver
      // This should create a trip with status "on_the_way" that shows up in trips/ongoing
      const tripRequestId = tripRequestIdRef.current || data.data?.id;
      if (tripRequestId) {
        console.log("🚀 Driver accepted trip request. Starting trip:", tripRequestId);

        // Remove driver from map
        const driverId = (data.data as any)?.driver_id || watchDriverId;
        if (driverId) {
          setDriverLocation(prev => prev.filter(d => d.id !== driverId));
        }

        // Get driver location for the start request
        const acceptedDriver = driverId
          ? driverLocation.find(d => d.id === driverId)
          : null;

        // Prepare location data for start endpoint (matching mobile app format)
        const locationData: IStartTripRequestInput = {
          latitude: acceptedDriver?.latitude || currentLocation.lat,
          longitude: acceptedDriver?.longitude || currentLocation.lng,
          accuracy: 0,
          speed: 0,
          local_timestamp: new Date().toISOString(),
          heading: 0,
        };

        // Call start endpoint - this should create a trip with status "on_the_way"
        startTripRequestByIdApi(tripRequestId, locationData)
          .then(({ data: startData, error: startError }) => {
            if (startData) {
              showSuccess(data.message || "Trip started successfully. Driver has been notified.");
            } else {
              const isAuthError = startError?.status === 401 || startError?.status === 403;
              if (isAuthError) {
                showSuccess(data.message || "Trip accepted successfully.");
              } else {
                showError(startError?.message || "Trip accepted but start failed. Driver may not see trip.");
              }
            }
          })
          .catch((error) => {
            console.error("❌ Exception starting trip request:", error);
            showSuccess(data.message || "Trip accepted successfully.");
          });
      } else {
        showSuccess(data.message);
      }

      // Reset cancellation flag and trip request ID after a delay
      setTimeout(() => {
        isCancelledRef.current = false;
        tripRequestIdRef.current = null;
      }, 2000);
    } else if (data.status === RequestStatus.already_on_trip || data.status === RequestStatus.cancelled) {
      // Driver declined or cancelled the trip
      setRequestLoading({ loading: false });
      isCancelledRef.current = false; // Reset cancellation flag
      tripRequestIdRef.current = null; // Reset trip request ID
      showError(data.message);
    } else if (data.status === RequestStatus.timeout) {
      // Driver timed out
      setRequestLoading({ loading: false });
      isCancelledRef.current = false;
      tripRequestIdRef.current = null;
      showError(data.message || "Trip request timed out.");
    } else {
      // Any other status stops loading and shows error
      setRequestLoading({ loading: false });
      isCancelledRef.current = false; // Reset cancellation flag
      tripRequestIdRef.current = null; // Reset trip request ID
      showError(data.message || "An error occurred during dispatch");
    }
  }, []); // eslint-disable-line

  const onTripStatusUpdate = useCallback((data: any) => {

    // Unconditionally remove driver if accepted (regardless of who dispatched)
    if (data.status === 'ACCEPTED' || data.status === RequestStatus.accepted) {
      const driverId = data.driver_id || (data.driver ? data.driver.id : null);
      if (driverId) {
        setDriverLocation(prev => {
          const exists = prev.find(d => d.id === driverId);
          if (exists) {
            console.log("🚫 Removing driver from map (status update):", driverId);
            return prev.filter(d => d.id !== driverId);
          }
          return prev;
        });
      }
    }

    // Check if this update matches our current request for notifications
    if (tripRequestIdRef.current && (data.trip_id === tripRequestIdRef.current || data.id === tripRequestIdRef.current)) {
      if (data.status === 'ACCEPTED' || data.status === RequestStatus.accepted) {
        setRequestLoading({ loading: false });
        showSuccess("Trip accepted successfully.");

        // Clear request ID
        tripRequestIdRef.current = null;
        isCancelledRef.current = false;

      } else if (data.status === 'TIMEOUT' || data.status === RequestStatus.timeout) {
        setRequestLoading({ loading: false });
        showError("Trip request timed out.");
        tripRequestIdRef.current = null;
        isCancelledRef.current = false;

      } else if (data.status === 'CANCELLED' || data.status === RequestStatus.cancelled) {
        setRequestLoading({ loading: false });
        showError("Trip request cancelled.");
        tripRequestIdRef.current = null;
        isCancelledRef.current = false;
      }
    }
  }, []);

  const onTripError = useCallback((data: any) => {
    if (isCancelledRef.current) return;

    if (data.status === RequestStatus.loading) {
      setRequestLoading({ loading: true, message: data.message });
    } else {
      // All error statuses stop loading and show error
      setRequestLoading({ loading: false });
      isCancelledRef.current = false; // Reset cancellation flag
      tripRequestIdRef.current = null; // Reset trip request ID
      showError(data.message || "An error occurred during dispatch");
    }
  }, []); // eslint-disable-line

  const fetchFleetModel = async () => {
    try {
      const { data, error } = await fleetModelsApi(1, 1000, "");
      if (data) {
        setModels({ data: data.data, loading: false });
      } else {
        showError(error?.message ?? "Failed to fetch fleet models");
        setModels((prev) => ({ ...prev, loading: false }));
      }
    } catch (err) {
      console.error("Error fetching fleet models:", err);
      showError("An unexpected error occurred while fetching fleet models");
      setModels((prev) => ({ ...prev, loading: false }));
    }
  };

  const onPickupChange = async (search: string, token: string) => {
    console.log("🔍 onPickupChange called with search:", search, "length:", search?.trim().length);
    if ((search ?? "").trim().length < 2) {
      console.log("⚠️ Search too short, clearing suggestions");
      setPickupSuggestion({ local: [] });
      setPickupSuggestionLoading(false);
      return;
    }
    if (activeMarkerType === undefined && !pickupLocationDisable) {
      setPickupSuggestionLoading(true);
      const { data, error } = await placeSearchApi(search, true);
      setPickupSuggestionLoading(false);
      if (data) {
        setPickupSuggestion({
          local: data.data.map((value) => ({
            label: value.name,
            value: value.name,
            title: { lat: value.latitude, lng: value.longitude } as any,
          }))
        });
      } else {
        setPickupSuggestion({ local: [] });
      }
    } else {
      setPickupSuggestionLoading(false);
    }
  };

  const onDropOffChange = async (search: string, token: string) => {
    console.log("🔍 onDropOffChange called with search:", search, "length:", search?.trim().length);
    if ((search ?? "").trim().length < 2) {
      console.log("⚠️ Search too short, clearing suggestions");
      setDropOffSuggestion({ local: [] });
      setDropOffSuggestionLoading(false);
      return;
    }
    if (activeMarkerType === undefined && !dropOffLocationDisable) {
      setDropOffSuggestionLoading(true);
      const { data, error } = await placeSearchApi(search, true);
      setDropOffSuggestionLoading(false);
      if (data) {
        setDropOffSuggestion({
          local: data.data.map((value) => ({
            label: value.name,
            value: value.name,
            title: { lat: value.latitude, lng: value.longitude } as any,
          }))
        });
      } else {
        setDropOffSuggestion({ local: [] });
      }
    } else {
      setDropOffSuggestionLoading(false);
    }
  };

  const onPickupSelect = async (
    name: string,
    {
      title,
      token,
    }: { title: string | { lat: number; lng: number }; token?: string }
  ) => {
    console.log("🔍 onPickupSelect called with:", {
      name,
      title,
      token,
      title_type: typeof title,
    });

    setPickupLocationDisable(true);
    if (
      typeof title === "object" &&
      !Array.isArray(title) &&
      title.lat &&
      title.lng
    ) {
      await new Promise((resolve) => setTimeout(resolve, 10));
      setValue("pickup_latitude", title.lat);
      setValue("pickup_longitude", title.lng);
      setValue("pickup_location", name);
      setPickupLocation({ lat: title.lat, lng: title.lng });
      setLocationName((prev) => ({ ...prev, pickup: name }));
    } else {
      showError("Invalid place selected. Please select a place from the list.");
    }
    setTimeout(() => setPickupLocationDisable(false), 10);
  };

  const onDropOffSelect = async (
    name: string,
    {
      title,
      token,
    }: { title: string | { lat: number; lng: number }; token?: string }
  ) => {
    console.log("🔍 onDropOffSelect called with:", {
      name,
      title,
      token,
      title_type: typeof title,
    });

    setDropOffLocationDisable(true);
    if (
      typeof title === "object" &&
      !Array.isArray(title) &&
      title.lat &&
      title.lng
    ) {
      await new Promise((resolve) => setTimeout(resolve, 10));
      setValue("drop_off_latitude", title.lat);
      setValue("drop_off_longitude", title.lng);
      setValue("drop_off_location", name);
      setDropOffLocation({ lat: title.lat, lng: title.lng });
      setLocationName((prev) => ({ ...prev, dropOff: name }));
    } else {
      showError("Invalid place selected. Please select a place from the list.");
    }
    setTimeout(() => setDropOffLocationDisable(false), 10);
  };

  const onSubmit = async (data: IDispatchInput) => {
    if (!socket || !socket.connected) {
      showError("Socket connection is not available. Please refresh the page.");
      return;
    }

    const pickupAddr = data.pickup_location || locationName.pickup;
    const dropOffAddr = data.drop_off_location || locationName.dropOff;

    if (!pickupAddr || !dropOffAddr) {
      showError("Pickup and drop-off addresses are required");
      return;
    }

    const pickupLat = Number(data.pickup_latitude);
    const pickupLng = Number(data.pickup_longitude);
    const dropOffLat = Number(data.drop_off_latitude);
    const dropOffLng = Number(data.drop_off_longitude);

    if (isNaN(pickupLat) || isNaN(pickupLng) || isNaN(dropOffLat) || isNaN(dropOffLng)) {
      showError("Invalid location coordinates. Please select locations from the list.");
      return;
    }

    const currentWatchDriverId = watch("driver_id");
    const driverId = data.driver_id || currentWatchDriverId || watchDriverId || null;

    isCancelledRef.current = false;
    tripRequestIdRef.current = null;
    setRequestLoading({ loading: true });

    const input: IDispatchTripInput = {
      driver_id: driverId,
      drop_off_location: { latitude: dropOffLat, longitude: dropOffLng },
      pickup_location: { latitude: pickupLat, longitude: pickupLng },
      fleet_model_id: data.fleet_model_id,
      passenger_first_name: data.passenger_first_name,
      passenger_last_name: data.passenger_last_name,
      passenger_phone: `+251${data.passenger_phone}`,
      pickup_address: pickupAddr,
      drop_off_address: dropOffAddr,
    };

    try {
      if (driverId) {
        // FORCED MANUAL DISPATCH via REST
        console.log("🎯 Triggering Sniper (Manual Assign) via REST for driver:", driverId);
        const { data: response, error } = await manualAssignApi(input);
        
        if (response) {
          // Success from REST
          setRequestLoading({ loading: true, message: "Manual dispatch sent. Waiting for driver..." });
          if (response.trip_request_id) {
            tripRequestIdRef.current = response.trip_request_id;
          }
          showSuccess(response.message || "Manual dispatch request sent successfully.");
        } else {
          // Error from REST
          setRequestLoading({ loading: false });
          showError(error?.message || "Failed to manually assign driver.");
        }
      } else {
        // STANDARD DISPATCH via SOCKET
        socket.emit("trip-request", input);
      }
    } catch (error: any) {
      setRequestLoading({ loading: false });
      showError("Failed to send trip request. Please try again.");
    }
  };

  const handleSetDriver = (driver: IDriverLocation) => {
    console.log("👤 Driver selected on map:", {
      driver_id: driver.id,
      driver_name: `${driver.first_name} ${driver.last_name}`,
      fleet_model_id: driver.fleet.fleet_model.id,
      fleet_model_name: driver.fleet.fleet_model.name,
    });

    // Set both values and ensure they're properly registered
    setValue("driver_id", driver.id, {
      shouldValidate: false,
      shouldDirty: false,
      shouldTouch: false
    });
    setValue("fleet_model_id", driver.fleet.fleet_model.id, {
      shouldValidate: false,
      shouldDirty: false,
      shouldTouch: false
    });

    // Use setTimeout to check the value after state update
    setTimeout(() => {
      const currentDriverId = watch("driver_id");
      console.log("✅ Driver ID set in form. Current watchDriverId:", currentDriverId);
    }, 0);
  };

  // FIX P2: Enhanced driver selection with info panel
  const handleSetDriverWithInfo = (driver: IDriverLocation) => {
    handleSetDriver(driver);
    setSelectedDriverInfo(driver);
  };

  // FIX P2: Driver search handler
  const handleDriverSearch = (query: string) => {
    setDriverSearchQuery(query);
  };

  // FIX P2: Filtered driver list for search
  const filteredDrivers = driverSearchQuery.trim()
    ? driverLocation.filter(d => {
        const q = driverSearchQuery.toLowerCase();
        const fullName = `${d.first_name} ${d.middle_name} ${d.last_name}`.toLowerCase();
        const plate = d.fleet?.plate_number?.toLowerCase() || '';
        const model = d.fleet?.fleet_model?.name?.toLowerCase() || '';
        return fullName.includes(q) || plate.includes(q) || model.includes(q);
      })
    : driverLocation;
  const handleClearDriver = () => {
    setValue("driver_id", null);
  };

  const handleMapClick = async (event: any) => {
    if (activeMarkerType === "pickup") {
      // Only set coordinates - no Google geocoding
      // Users must select a place from the places list
      setPickupLocation({
        lat: event.detail.latLng.lat,
        lng: event.detail.latLng.lng,
      });
      // Clear suggestions when clicking on map
      setPickupSuggestion({ local: [] });

      // Only set coordinates - don't use Google addresses
      setValue("pickup_latitude", event.detail.latLng.lat);
      setValue("pickup_longitude", event.detail.latLng.lng);
      // Set location name as coordinates temporarily until user selects from places list
      const coordString = `${event.detail.latLng.lat.toFixed(6)}, ${event.detail.latLng.lng.toFixed(6)}`;
      setValue("pickup_location", coordString);
      setLocationName((prev) => ({
        ...prev,
        pickup: coordString,
      }));
    } else if (activeMarkerType === "destination") {
      // Only set coordinates - no Google geocoding
      // Users must select a place from the places list
      setDropOffLocation({
        lat: event.detail.latLng.lat,
        lng: event.detail.latLng.lng,
      });
      // Clear suggestions when clicking on map
      setDropOffSuggestion({ local: [] });

      // Only set coordinates - don't use Google addresses
      setValue("drop_off_latitude", event.detail.latLng.lat);
      setValue("drop_off_longitude", event.detail.latLng.lng);
      // Set location name as coordinates temporarily until user selects from places list
      const coordString = `${event.detail.latLng.lat.toFixed(6)}, ${event.detail.latLng.lng.toFixed(6)}`;
      setValue("drop_off_location", coordString);
      setLocationName((prev) => ({
        ...prev,
        dropOff: coordString,
      }));
    }

    setTimeout(() => {
      setActiveMarkerType(undefined);
    }, 1);
  };

  const handleTogglePickupLocation = () => {
    if (activeMarkerType === "destination" || activeMarkerType === undefined)
      setActiveMarkerType("pickup");
    else setActiveMarkerType(undefined);
  };

  const handleToggleDestinationLocation = () => {
    if (activeMarkerType === "pickup" || activeMarkerType === undefined)
      setActiveMarkerType("destination");
    else setActiveMarkerType(undefined);
  };

  const handleCancelTrip = async () => {
    console.log("🚫 Cancel request clicked");

    // Mark as cancelled to ignore future socket responses
    isCancelledRef.current = true;

    // Stop loading overlay immediately
    setRequestLoading({ loading: false });

    // First, try to cancel via socket (works even without trip request ID)
    if (socket && socket.connected) {
      console.log("🔄 Emitting cancel-trip-request socket event");
      try {
        socket.emit("cancel-trip-request", {
          trip_request_id: tripRequestIdRef.current || undefined,
          reason: "Cancelled by dispatcher"
        });
        console.log("✅ Cancel socket event emitted");
      } catch (error) {
        console.error("❌ Error emitting cancel socket event:", error);
      }
    }

    // If we have a trip request ID, also cancel it via API for extra assurance
    const cancelViaApi = async (tripId: string) => {
      console.log("🔄 Attempting to cancel trip request via API:", tripId);
      try {
        const { data, error } = await cancelTripRequestByIdApi(tripId, {
          reason: "Cancelled by dispatcher"
        });

        if (data) {
          console.log("✅ Trip request cancelled successfully via API");
          showSuccess("Trip request cancelled successfully");
        } else {
          console.error("❌ Error cancelling trip request via API:", error?.message);
          // Don't show error if socket cancel might have worked
        }
      } catch (error: any) {
        console.error("❌ Exception cancelling trip request via API:", error);
        // Don't show error if socket cancel might have worked
      }
    };

    if (tripRequestIdRef.current) {
      // We have the ID, cancel immediately
      await cancelViaApi(tripRequestIdRef.current);
    } else {
      // Don't have ID yet - wait a bit to see if we get it from socket response
      console.log("⚠️ No trip request ID yet, waiting 1 second for socket response...");
      const checkInterval = setInterval(() => {
        if (tripRequestIdRef.current) {
          clearInterval(checkInterval);
          cancelViaApi(tripRequestIdRef.current);
        }
      }, 200);

      // Give up after 2 seconds
      setTimeout(() => {
        clearInterval(checkInterval);
        if (!tripRequestIdRef.current) {
          console.log("⚠️ Trip request ID not received after 2 seconds");
          showSuccess("Dispatch request cancelled (may still be processing on backend)");
        }
      }, 2000);
    }

    // Reset cancellation state after delay
    setTimeout(() => {
      tripRequestIdRef.current = null;
      isCancelledRef.current = false;
    }, 3000);

    console.log("✅ Request cancellation initiated");
  };

  const handleClearForm = async () => {
    setValue("driver_id", null);
    setValue("drop_off_latitude", null as any);
    setValue("drop_off_location", null as any);
    setValue("drop_off_longitude", null as any);
    setValue("fleet_model_id", null as any);
    setValue("passenger_first_name", null as any);
    setValue("passenger_last_name", null as any);
    setValue("passenger_phone", null as any);
    setValue("pickup_latitude", null as any);
    setValue("pickup_latitude", null as any);
    setValue("pickup_location", null as any);
    setValue("pickup_longitude", null as any);
    setLocationName({ pickup: undefined, dropOff: undefined });
    setTripEstimation(undefined);
  };

  // Create onSearch handlers that can be called directly from AutoComplete
  const onPickupSearch = (search: string) => {
    console.log("🔎 onPickupSearch called with:", search);
    if (!search || search.trim().length < 2) {
      console.log("⚠️ Search too short, clearing suggestions");
      setPickupSuggestion({ local: [] });
      return;
    }
    const token = pickupToken ?? `${Math.random().toString(36).substring(2)}-${Date.now()}`;
    if (!pickupToken) setPickupToken(token);
    if (pickupDebounceTimer !== undefined) clearTimeout(pickupDebounceTimer);
    setPickupDebounceTimer(
      setTimeout(() => {
        console.log("⏰ Debounce timeout, calling onPickupChange");
        onPickupChange(search.trim(), token);
      }, 300)
    );
  };

  const onDropOffSearch = (search: string) => {
    console.log("🔎 onDropOffSearch called with:", search);
    if (!search || search.trim().length < 2) {
      console.log("⚠️ Search too short, clearing suggestions");
      setDropOffSuggestion({ local: [] });
      return;
    }
    const token = dropOffToken ?? `${Math.random().toString(36).substring(2)}-${Date.now()}`;
    if (!dropOffToken) setDropOffToken(token);
    if (dropOffDebounceTimer !== undefined) clearTimeout(dropOffDebounceTimer);
    setDropOffDebounceTimer(
      setTimeout(() => {
        console.log("⏰ Debounce timeout, calling onDropOffChange");
        onDropOffChange(search.trim(), token);
      }, 300)
    );
  };

  return {
    control,
    models,
    pickupSuggestion: pickupSuggestion.local, // Only return local places
    pickupSuggestionLoading,
    dropOffSuggestion: dropOffSuggestion.local, // Only return local places
    dropOffSuggestionLoading,
    driverLocation,
    currentLocation,
    requestLoading,
    watchDriverId,
    pickupLocation,
    dropOffLocation,
    activeMarkerType,
    tripEstimation,
    handleCancelTrip,
    handleSetDriver: handleSetDriverWithInfo, // FIX P2: Use enhanced handler
    handleClearDriver,
    handleSubmit,
    handleTogglePickupLocation,
    handleToggleDestinationLocation,
    onSubmit,
    onDropOffSelect,
    onPickupSearch,
    onDropOffSearch,
    handleMapClick,
    handleClearForm,
    onPickupSelect,
    // FIX P2: New exports for enhanced dispatch UI
    selectedDriverInfo,
    dispatchProgress,
    driverSearchQuery,
    filteredDrivers,
    handleDriverSearch,
  };
}
